/**
 * DesignIO - Design save/load, export and autosave
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * Handles SVG/DXF export of the cut pattern, JSON (de)serialization of the
 * design, file save/open dialogs, and debounced localStorage autosave.
 */
import { SketchSpline } from '../sketch/SketchSpline.js';
import { CrossSliceSelection } from '../geometry/CrossSliceSelection.js';
import { collectParts, buildSVG } from '../export/SVGExporter.js';
import { buildDXF } from '../export/DXFExporter.js';
import { DESIGN_SCHEMA_VERSION } from './schema/version.js';
import {
    migrateToLatest,
    strokesFromLegacySketch,
    SchemaTooNewError
} from './schema/migrations.js';

export class DesignIO {
    constructor(app) {
        this.app = app;
    }

    /**
     * Trigger a browser download of a text document
     */
    _downloadText(text, filename, mime) {
        const blob = new Blob([text], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }

    /**
     * Export the cut pattern as SVG (all parts with slots cut, packed onto
     * fixed-size sheets; units are millimetres). Parts that overflow the
     * sheet height spill onto extra pages: with more than one page, one
     * file per page is downloaded (cut-pattern-page1.svg, ...).
     * @param {boolean} [download=true] - Trigger file download(s)
     * @param {Object} [packOpts] - ShapePacker options ({sheetWidth,
     *   sheetHeight, gap, margin}); merged over the app's fabrication settings
     * @returns {{svg: string, partCount: number, pageCount: number, pages: Array}|null}
     */
    exportSVG(download = true, packOpts = {}) {
        const opts = { ...(this.app.fabSettings || {}), ...packOpts };
        const parts = collectParts(
            this.app.basePlanes,
            this.app.basePlaneSketchStates,
            this.app.slicePlaneManager?.planes || [],
            { scale: opts.scale ?? 1 }
        );

        if (parts.length === 0) {
            console.warn('exportSVG: nothing to export');
            return null;
        }

        // Paginate: one SVG document per sheet page
        const first = buildSVG(parts, { ...opts, page: 0 });
        const pages = [first];
        for (let i = 1; i < first.pageCount; i++) {
            pages.push(buildSVG(parts, { ...opts, page: i }));
        }

        const totalParts = pages.reduce((sum, p) => sum + p.partCount, 0);
        console.log(`SVG export: ${totalParts} parts across ${first.pageCount} page(s), ` +
            `${first.widthMM}x${first.heightMM}mm`);

        if (download) {
            pages.forEach((p, i) => {
                const name = first.pageCount > 1 ? `cut-pattern-page${i + 1}.svg` : 'cut-pattern.svg';
                this._downloadText(p.svg, name, 'image/svg+xml');
            });
        }

        return { ...first, partCount: totalParts, pages };
    }

    /**
     * Export the cut pattern as DXF R12 (CNC/laser toolchains). Paginated
     * like exportSVG: more than one page downloads one file per page
     * (cut-pattern-page1.dxf, ...).
     * @param {boolean} [download=true]
     * @param {Object} [packOpts] - ShapePacker options ({sheetWidth,
     *   sheetHeight, gap, margin}); merged over the app's fabrication settings
     * @returns {{dxf: string, partCount: number, pageCount: number, pages: Array}|null}
     */
    exportDXF(download = true, packOpts = {}) {
        const opts = { ...(this.app.fabSettings || {}), ...packOpts };
        const parts = collectParts(
            this.app.basePlanes,
            this.app.basePlaneSketchStates,
            this.app.slicePlaneManager?.planes || [],
            { scale: opts.scale ?? 1 }
        );
        if (parts.length === 0) {
            console.warn('exportDXF: nothing to export');
            return null;
        }

        // Paginate: one DXF document per sheet page
        const first = buildDXF(parts, { ...opts, page: 0 });
        const pages = [first];
        for (let i = 1; i < first.pageCount; i++) {
            pages.push(buildDXF(parts, { ...opts, page: i }));
        }

        const totalParts = pages.reduce((sum, p) => sum + p.partCount, 0);
        console.log(`DXF export: ${totalParts} parts across ${first.pageCount} page(s), ` +
            `${first.widthMM}x${first.heightMM}mm`);

        if (download) {
            pages.forEach((p, i) => {
                const name = first.pageCount > 1 ? `cut-pattern-page${i + 1}.dxf` : 'cut-pattern.dxf';
                this._downloadText(p.dxf, name, 'application/dxf');
            });
        }

        return { ...first, partCount: totalParts, pages };
    }

    /**
     * Serialize the whole design to a plain JSON object.
     *
     * Schema v2 (LINKED MASTER STROKES): the authoritative payload is
     * `strokes` - the document-level stroke MASTERS plus the per-layer
     * INSTANCE tables ({id, linked} refs or detached standalone shapes).
     * A legacy `sketch` section (the live sketch, with legs pulled from an
     * outer layer if needed) is kept alongside so older builds can still
     * open the file as a v1-style single sketch.
     */
    serializeDesign() {
        // Legs live only on the OUTER layers' stored states. If the live
        // sketch is currently an inner layer's (no legs), pull the legs from
        // an outer plane's state so the legacy sketch section keeps them.
        const isLeg = (s) => s.type === SketchSpline.TYPE_LEG;
        let splines = this.app.sketchPlane.splines;
        if (!splines.some(isLeg)) {
            const states = this.app.basePlaneSketchStates || [];
            const outerWithLegs = [states[0], states[states.length - 1]]
                .find(st => (st?.splines || []).some(isLeg));
            if (outerWithLegs) {
                splines = [...splines, ...outerWithLegs.splines.filter(isLeg)];
            }
        }

        return {
            format: 'sketchchair-js',
            version: DESIGN_SCHEMA_VERSION,
            savedAt: new Date().toISOString(),
            settings: {
                brushWidth: this.app.sketchPlane.brushWidth,
                numBasePlanes: this.app.numBasePlanes,
                basePlaneSpacing: this.app.basePlaneSpacing,
                // Where the user parked the chair with the hand tool, as an
                // offset from its generated pose. TRANSLATION ONLY, and by
                // design: a simulated pose (tipped, slid, fallen) can never be
                // expressed here, so a design file cannot store one. The
                // solver's pose is transient and is never serialized.
                chairPlacement: {
                    x: this.app.chairMoveOffset.x,
                    y: this.app.chairMoveOffset.y
                }
            },
            sketch: {
                paths: this.app.sketchPlane.paths.map(p => p.toJSON()),
                splines: splines.map(sp => sp.toJSON())
            },
            strokes: this.app.strokeStore ? this.app.strokeStore.toJSON() : null,
            // Slice groups + their settings (rib start/end/spacing, cap style,
            // options and - the reason this was added - per-leg crossbrace
            // toggle/width/offset). Without this the pipeline regenerates them
            // from scratch on load, resetting every setting to its default.
            crossSliceSelections: (this.app.crossSliceSelections || []).map(s => s.toJSON())
        };
    }

    /**
     * Load a serialized design (replaces the current sketch)
     *
     * EVERY read path runs migrateToLatest first (spec §9.3): legacy
     * payloads are upgraded to the current schema as a pure JSON transform
     * (v1's single sketch becomes one master per stroke, linked on all
     * layers), then a single current loader builds the runtime. Payloads
     * from a NEWER app fail gracefully (SchemaTooNewError).
     * @param {Object} data - Object produced by serializeDesign()
     * @returns {boolean} True on success
     */
    loadDesign(data) {
        if (!data || data.format !== 'sketchchair-js' || (!data.sketch && !data.strokes)) {
            console.error('loadDesign: not a SketchChair design file');
            return false;
        }

        try {
            data = migrateToLatest(data);
        } catch (error) {
            if (error instanceof SchemaTooNewError) {
                console.error('loadDesign:', error.message);
                this.app.showToast?.(error.message);
            } else {
                console.error('loadDesign: migration failed:', error);
            }
            return false;
        }

        // Current-schema files written without a stroke store (strokes:null)
        // still carry the legacy sketch section - synthesize the store
        // payload from it with the same transform the v1 migrator uses
        if (!data.strokes || !Array.isArray(data.strokes.layers)) {
            data = { ...data, strokes: strokesFromLegacySketch(data) };
        }

        return this._loadCurrent(data);
    }

    /**
     * Loader for the CURRENT schema only (migrations guarantee the payload
     * is at DESIGN_SCHEMA_VERSION): restore the stroke store (links +
     * detachments intact)
     */
    _loadCurrent(data) {
        try {
            this.app.pushUndoSnapshot();

            const sp = this.app.sketchPlane;
            sp.cancelCurrentDrawing();

            // Restore where the hand tool parked this chair (files written
            // before chairPlacement existed, and chairs never moved, land at
            // their generated pose). Set BEFORE the regeneration the load
            // triggers: the pipeline re-applies the offset once the compound
            // body is rebuilt. Never inherit the outgoing chair's parking.
            const placement = data.settings?.chairPlacement;
            this.app.chairMoveOffset.set(
                Number(placement?.x) || 0,
                Number(placement?.y) || 0,
                0
            );

            // Match the saved layer count before installing the store
            const layerCount = data.strokes.layers.length ||
                data.settings?.numBasePlanes || this.app.numBasePlanes;
            if (layerCount !== this.app.basePlanes.length && layerCount > 0) {
                this.app.numBasePlanes = layerCount;
                this.app.basePlaneSpacing =
                    this.app.chairWidth / Math.max(1, layerCount - 1);
                // Rebuilds planes and resets the store; we overwrite it next
                this.app.layerManager.initializeBasePlanes();
            }

            if (!this.app.strokeStore.loadJSON(data.strokes, sp)) {
                console.error('loadDesign: bad strokes payload');
                return false;
            }
            this.app.strokeStore.ensureLayerCount(this.app.basePlanes.length);

            if (data.settings?.brushWidth) {
                sp.brushWidth = data.settings.brushWidth;
            }

            // Select all planes and show the first layer's resolved sketch
            this.app.selectedBasePlaneIndices.clear();
            this.app.basePlanes.forEach((p, i) => {
                this.app.selectedBasePlaneIndices.add(i);
                p.select();
            });
            this.app.resolveLayerStates();
            this.app.loadLayerIntoSketch(0);
            sp.selectedSpline = null;
            sp.selectedPath = null;
            sp.selectedShapes?.clear();

            // Restore saved slice groups BEFORE regeneration so the pipeline
            // reuses them (rib groups matched by presence, leg braces by
            // legIndex) instead of recreating defaults - this is what keeps
            // the leg crossbrace toggle/width/offset (and every other slice
            // setting) across a reload. Runtime path/plane refs are re-resolved
            // during the regen onShapeChange triggers.
            if (Array.isArray(data.crossSliceSelections)) {
                this.app.crossSliceSelections =
                    data.crossSliceSelections.map(d => CrossSliceSelection.fromJSON(d));
            }

            sp.onShapeChange();
            this.app.updateStatusDisplay?.();
            console.log('Design loaded (v2):',
                this.app.strokeStore.masters.size, 'stroke master(s) across',
                this.app.strokeStore.layerCount, 'layer(s)');
            return true;
        } catch (error) {
            console.error('loadDesign failed:', error);
            return false;
        }
    }

    /**
     * Download the design as a .sketchchair.json file (Ctrl+S)
     */
    saveDesignToFile() {
        const json = JSON.stringify(this.serializeDesign(), null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'design.sketchchair.json';
        a.click();
        URL.revokeObjectURL(url);
        console.log('Design saved to file');
    }

    /**
     * Open a design file via file picker (Ctrl+O)
     */
    loadDesignFromFile() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,application/json';
        input.onchange = () => {
            const file = input.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    this.loadDesign(JSON.parse(reader.result));
                } catch (error) {
                    console.error('Could not parse design file:', error);
                }
            };
            reader.readAsText(file);
        };
        input.click();
    }

    /**
     * Autosave to localStorage (debounced; called on every shape change)
     */
    autosaveDesign() {
        clearTimeout(this._autosaveTimer);
        this._autosaveTimer = setTimeout(() => {
            try {
                localStorage.setItem('sketchchair-autosave', JSON.stringify(this.serializeDesign()));
            } catch (error) {
                console.warn('Autosave failed:', error);
            }
        }, 600);
    }

    /**
     * Restore the last autosaved design, if any (called at startup,
     * matching the Java version's autoLoad)
     */
    restoreAutosave() {
        try {
            const raw = localStorage.getItem('sketchchair-autosave');
            if (!raw) return;
            const data = JSON.parse(raw);
            const hasV2Content = (data?.strokes?.masters?.length || 0) > 0 ||
                (data?.strokes?.layers || []).some(l => (l?.length || 0) > 0);
            const hasV1Content = data?.sketch &&
                ((data.sketch.splines?.length || 0) + (data.sketch.paths?.length || 0)) > 0;
            if (hasV2Content || hasV1Content) {
                console.log('Restoring autosaved design from', data.savedAt);
                this.loadDesign(data);
                // The restore itself should not be undoable back to nothing
                this.app.undoManager.clear();
            }
        } catch (error) {
            console.warn('Could not restore autosave:', error);
        }
    }
}
